import React from 'react';
import { useAuth } from '../context/auth-provider';

// Frontend mirror of the backend permission catalog (src/authz/permissions.js).
// The authoritative permission SET for the signed-in user comes from the server
// (login / signup / accept / /auth responses) and lives in auth context; these
// constants exist only so call sites read `PERMISSIONS.BUILDING_CREATE` instead
// of a bare string, and can()/Can decide UI visibility from that server set.
export const PERMISSIONS = {
  BUILDING_READ: 'building:read',
  BUILDING_CREATE: 'building:create',
  BUILDING_EDIT: 'building:edit',
  BUILDING_DELETE: 'building:delete',
  // Independent Stays IS7: give a host a link code, decide, end. Owner, manager.
  BUILDING_LINK_HOSTS: 'building:link_hosts',

  PORTFOLIO_READ: 'portfolio:read',
  PORTFOLIO_CREATE: 'portfolio:create',
  PORTFOLIO_EDIT: 'portfolio:edit',
  PORTFOLIO_DELETE: 'portfolio:delete',

  UNIT_READ: 'unit:read',
  UNIT_CREATE: 'unit:create',
  UNIT_EDIT: 'unit:edit',
  UNIT_DELETE: 'unit:delete',

  OCCUPANCY_READ: 'occupancy:read',
  OCCUPANCY_CREATE: 'occupancy:create',
  OCCUPANCY_EDIT: 'occupancy:edit',

  // Access grants / derived access_scope. `scope:read` is "see who has access
  // to what"; `scope:manage` is "grant or revoke it", which the backend holds
  // to owners only — delegating the ability to delegate access is an
  // escalation path.
  SCOPE_READ: 'scope:read',
  SCOPE_MANAGE: 'scope:manage',

  USER_READ: 'user:read',
  USER_CREATE: 'user:create',
  USER_EDIT: 'user:edit',
  USER_DELETE: 'user:delete',

  TENANT_READ: 'tenant:read',
  TENANT_CREATE: 'tenant:create',
  // WP-31. Owners and property managers, matching tenant:create. It is NOT
  // tenant:delete's audience: removing a person from the organisation stays with
  // the owner, while correcting the name somebody typed is day-to-day work for
  // the role that typed it.
  TENANT_EDIT: 'tenant:edit',
  TENANT_DELETE: 'tenant:delete',

  INVITE_CREATE: 'invite:create',
  INVITE_READ: 'invite:read',
  INVITE_RESEND: 'invite:resend',
  INVITE_REVOKE: 'invite:revoke',

  VISITORLOG_CREATE: 'visitorlog:create',
  VISITORLOG_READ: 'visitorlog:read',
  // Owners and property managers only. A concierge holds no delete capability
  // of any kind: the role that writes the log must not be the role that can
  // make an entry disappear.
  VISITORLOG_DELETE: 'visitorlog:delete',
  DAILYLOG_CREATE: 'dailylog:create',
  DAILYLOG_READ: 'dailylog:read',
  DAILYLOG_DELETE: 'dailylog:delete',

  // The parcel desk. package:release is separate from package:edit because
  // handing a parcel over is the consequential act — the one that can go to the
  // wrong Maria — and correcting a shelf number should not be the same
  // capability. package:delete is owner/manager only, matching the logs.
  PACKAGE_READ: 'package:read',
  PACKAGE_CREATE: 'package:create',
  PACKAGE_EDIT: 'package:edit',
  PACKAGE_RELEASE: 'package:release',
  PACKAGE_DELETE: 'package:delete',

  // Visitor passes (resident-portal Plan 2). Mirrors src/authz/permissions.js:
  // read = manager + concierge, manage = manager, desk_write = the concierge's
  // own writes, as far as each building allows (concierge_pass_access,
  // migration 118). The owner holds all three; residents none.
  VISITORPASS_READ: 'visitorpass:read',
  VISITORPASS_MANAGE: 'visitorpass:manage',
  VISITORPASS_DESK_WRITE: 'visitorpass:desk_write',

  // Lobbero Stays (plan S1). Mirrors src/authz/permissions.js: owner holds all
  // six; property manager every one except STAYS_PAYMENTS ("owner sees
  // billing"); concierge STAYS_DESK only; residents none. Whether the
  // ORGANISATION has Stays is a separate server check (useStaysEntitlement).
  STAYS_READ: 'stays:read',
  STAYS_PROMOTE: 'stays:promote',
  STAYS_MANAGE: 'stays:manage',
  STAYS_DESK: 'stays:desk',
  STAYS_COMPLIANCE: 'stays:compliance',
  STAYS_PAYMENTS: 'stays:payments',
  // Independent Stays IS5 (migration 180). LISTING_EDIT: edit, set up, pause,
  // resume and retire a listing (owner, property manager, co-host) — promoting a
  // unit and Properties stay STAYS_PROMOTE. GUEST_CARDS and COMPLIANCE_READ:
  // the co-host's own view (owner and manager hold them too).
  STAYS_LISTING_EDIT: 'stays:listing_edit',
  STAYS_GUEST_CARDS: 'stays:guest_cards',
  STAYS_COMPLIANCE_READ: 'stays:compliance_read',
  // Independent Stays IS9: a hotel's reception writes (rooms, check-in / out,
  // walk-ins). Owner, manager, concierge ("Reception").
  STAYS_RECEPTION: 'stays:reception',

  REPORT_READ: 'report:read',
  REPORT_EXPORT: 'report:export',

  PLAN_READ: 'plan:read',
  BILLING_READ: 'billing:read',
  BILLING_MANAGE: 'billing:manage',

  EMAIL_RESEND_VERIFICATION: 'email:resend_verification',
  PROFILE_EDIT: 'profile:edit',
  PORTAL_READ: 'portal:read',

  // Privacy requests — find, export and erase everything the organisation holds
  // about one person. Owner only (the backend grants it to no other role).
  PRIVACY_REQUESTS: 'privacy:requests',

  // Lobbero IQ (the AI handover surface). A role capability, in lockstep with the
  // backend catalog; org entitlement (subscriptions.iq_enabled) is a separate
  // check the server owns. Without this the tile renders and the API 403s.
  IQ_USE: 'iq:use',

  // The account-level half. An owner holds it and a manager does not: the
  // reasons IQ can be OFF for an organisation (no key, a tier that has not been
  // attested, a spent budget) are things only this reader can act on, so the
  // entry point stays visible to them when it is hidden from everybody else.
  IQ_ADMIN: 'iq:admin',

  // The shift clock (backend catalog: permissions.js:101 / :106).
  //
  // THE FRONT DESK IS THE ROLE THAT CLOCKS IN, so a concierge holds
  // SHIFT_CLOCK — the control lives on the main menu, and a desk without the
  // capability would otherwise see a button that 403s (permissions.js:191-193).
  //
  // SHIFT_MANAGE IS NOT THE SAME GATE AND MUST NOT BE COLLAPSED INTO IT.
  // Self-resolve is always allowed, or a forgotten clock-out becomes a support
  // ticket; resolving SOMEBODY ELSE'S shift needs SHIFT_MANAGE, which a
  // concierge deliberately does not hold — the role that works the desk must
  // not be able to close a colleague's attendance record (shifts.js:220-224).
  //
  // NOTE THE ASYMMETRY WITH THE MONITOR: /shifts/monitor is gated on USER_READ
  // above, NOT on SHIFT_CLOCK, because the second group it renders reports that
  // a named person has not done what they were supposed to. Gating both
  // surfaces on one constant would either hide the clock from the desk or
  // 403-spam the monitor.
  SHIFT_CLOCK: 'shift:clock',
  SHIFT_MANAGE: 'shift:manage',

  // The Analytics Hub (backend: src/analytics/hub.js, gate
  // `requirePermission(P.ANALYTICS_READ)` on all four routes). Owner and
  // property manager only — a concierge gets a 403, which is why the menu entry
  // and the route are both gated on this exact string rather than on a role
  // list: a tile that 403s is worse than no tile.
  //
  // NOT the same gate as REPORT_READ. Reports is the per-building daily/weekly
  // export; the hub is the cross-building aggregate surface, and the backend
  // gives them separate permissions so one can be widened without the other.
  ANALYTICS_READ: 'analytics:read',
} as const;

// Pure membership check — no React, safe to import anywhere.
export const can = (
  permissions: string[] | null | undefined,
  permission: string
): boolean => !!permissions && permissions.includes(permission);

export const canAny = (
  permissions: string[] | null | undefined,
  perms: string[]
): boolean => !!permissions && perms.some(p => permissions.includes(p));

export const canAll = (
  permissions: string[] | null | undefined,
  perms: string[]
): boolean => !!permissions && perms.every(p => permissions.includes(p));

// Hook bound to the current user's permission set. `useCan()('building:create')`.
export const useCan = (): ((permission: string) => boolean) => {
  const { permissions } = useAuth();

  return (permission: string) => can(permissions, permission);
};

// Declarative UI guard: renders children only if the user holds `permission`.
export const Can: React.FC<{
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permission, children, fallback = null }) => {
  const { permissions } = useAuth();

  return <>{can(permissions, permission) ? children : fallback}</>;
};
